import { useCallback } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login";
import { DisconnectShopifyResult, OnlineStorePublishSummary } from "./types";

/** Never throws — callers branch on `ok`. */
export type PublishRetryResult =
    | { ok: true; summary: OnlineStorePublishSummary }
    | { ok: false; message: string };

const errorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.message || err?.message || fallback;

// #region useShopifyPublish
/**
 * Online Store publishing + Shopify reconnect actions.
 *
 * - `disconnect` clears the brand's Shopify credentials (tokens, API key and
 *   secret) so it can reinstall the app with the new permissions. Product
 *   links are kept on the backend.
 * - `publishDesign` / `publishUsage` retry putting an existing product on the
 *   Online Store after a reconnect.
 */
export const useShopifyPublish = () => {
    const [user, authToken] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const disconnect = useCallback(async (): Promise<DisconnectShopifyResult | null> => {
        try {
            const response = await Axios.post(`${environment.brandChannelUrl}/disconnect-shopify`, {}, { headers });
            return response.data as DisconnectShopifyResult;
        } catch {
            return null;
        }
    }, [authToken]); // eslint-disable-line

    const publishDesign = useCallback(async (designId: number, brandId?: number): Promise<PublishRetryResult> => {
        try {
            const response = await Axios.post(
                `${environment.baseUrl}/api/v1/designs/release/${designId}/publish-online-store`,
                { brand_id: brandId ?? user?.brand_id },
                { headers }
            );
            return { ok: true, summary: response.data as OnlineStorePublishSummary };
        } catch (err: any) {
            return { ok: false, message: errorMessage(err, 'Could not publish to your Online Store.') };
        }
    }, [authToken, user?.brand_id]); // eslint-disable-line

    /**
     * Live Online Store status of a released design's products. Null when there
     * is nothing to report (no products, store unreachable, request failed).
     */
    const getDesignStatus = useCallback(async (designId: number, brandId?: number): Promise<OnlineStorePublishSummary | null> => {
        try {
            const response = await Axios.get(
                `${environment.baseUrl}/api/v1/designs/release/${designId}/online-store-status`,
                { headers, params: { brand_id: brandId ?? user?.brand_id } }
            );
            return (response.data?.online_store_publish ?? null) as OnlineStorePublishSummary | null;
        } catch {
            return null;
        }
    }, [authToken, user?.brand_id]); // eslint-disable-line

    const publishUsage = useCallback(async (usageId: number): Promise<PublishRetryResult> => {
        try {
            const response = await Axios.post(
                `${environment.catalogUrl}/usage/${usageId}/publish-online-store`,
                {},
                { headers }
            );
            return { ok: true, summary: response.data as OnlineStorePublishSummary };
        } catch (err: any) {
            return { ok: false, message: errorMessage(err, 'Could not publish to your Online Store.') };
        }
    }, [authToken]); // eslint-disable-line

    return { disconnect, publishDesign, getDesignStatus, publishUsage };
};
